import React, { useEffect, useState } from 'react';
import './HeroSlideshow.css';

interface Slide {
  id: number;
  image: string;
  title: string;
  subtitle: string;
  description?: string;
}

const slides: Slide[] = [
  {
    id: 2,
    image: '/images/slideshow/sunrise.jpg',
    title: 'Sustainable Future',
    subtitle: 'Building resilient communities through data-driven climate action.',
    description: 'Access cutting-edge climate data and planning resources'
  },
  {
    id: 3,
    image: '/images/slideshow/los-angeles.jpg',
    title: 'California Climate Action',
    subtitle: 'Empowering local governments with comprehensive climate tools.',
    description: 'From emissions tracking to renewable energy planning'
  }
];

export const HeroSlideshow: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Auto-advance slides every 8 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 8000);

    return () => clearInterval(timer);
  }, []);

  const goToSlide = (index: number) => {
    if (index === currentSlide || isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentSlide(index);
    
    // Reset transition state after animation
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const goToPrevious = () => {
    goToSlide(currentSlide === 0 ? slides.length - 1 : currentSlide - 1);
  };

  const goToNext = () => {
    goToSlide((currentSlide + 1) % slides.length);
  };

  const scrollToNextSection = () => {
    // Find the next section after the slideshow
    const nextSection = document.querySelector('.overview-content, .tool-section, .page-header');
    if (nextSection) {
      nextSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  return (
    <div className="hero-slideshow">
      {/* Main slideshow container */}
      <div className="slideshow-container">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`slide ${index === currentSlide ? 'active' : ''} ${
              index === currentSlide - 1 || (currentSlide === 0 && index === slides.length - 1) ? 'prev' : ''
            } ${
              index === currentSlide + 1 || (currentSlide === slides.length - 1 && index === 0) ? 'next' : ''
            }`}
            style={{
              backgroundImage: `url(${slide.image})`,
              transform: `translateX(${(currentSlide - index) * 100}%)`
            }}
          >
            <div className="slide-overlay" />
            <div className="slide-content">
              <div className="slide-text">
                <h1 className="slide-title">{slide.title}</h1>
                <p className="slide-subtitle">{slide.subtitle}</p>
                {slide.description && (
                  <p className="slide-description">{slide.description}</p>
                )}
              </div>
              <div className="slide-actions">
                <button className="cta-button primary" onClick={scrollToNextSection}>
                  Explorer Tools
                </button>
                {/* <button className="cta-button secondary">
                  Explore Tools
                  <span className="new-badge">N</span>
                </button> */}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation arrows */}
      <button className="nav-arrow nav-arrow-left" onClick={goToPrevious}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      <button className="nav-arrow nav-arrow-right" onClick={goToNext}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* Slide indicators */}
      <div className="slide-indicators">
        {slides.map((_, index) => (
          <button
            key={index}
            className={`indicator ${index === currentSlide ? 'active' : ''}`}
            onClick={() => goToSlide(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Progress bar */}
      <div className="progress-bar">
        <div 
          className="progress-fill" 
          style={{ 
            width: `${((currentSlide + 1) / slides.length) * 100}%`,
            transition: 'width 0.3s ease'
          }} 
        />
      </div>
    </div>
  );
};
